import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';
import api from '../../api';
import {
  Cable, Search, Loader2, ChevronLeft, ChevronRight, Eye, Edit2, Copy,
  Trash2, Plus, CircleDot, CircleOff, CircleMinus, Circle, CheckCircle2,
} from 'lucide-react';

const emptyForm = () => ({
  port_name: '', port_number: '', tipo_dispositivo_id: '', tipo_conexion_id: '',
  connected_ip: '', connected_hostname: '', vlan_id: '', user_description: '', user_notes: '',
});

const formFromPort = port => ({
  port_name: port.port_name || '',
  port_number: port.port_number?.toString() || '',
  tipo_dispositivo_id: port.tipo_dispositivo_id?.toString() || '',
  tipo_conexion_id: port.tipo_conexion_id?.toString() || '',
  connected_ip: port.connected_ip || '',
  connected_hostname: port.connected_hostname || '',
  vlan_id: port.doc_vlan?.toString() || '',
  user_description: port.user_description || '',
  user_notes: port.user_notes || '',
});

const formatDate = value => {
  if (!value) return 'Sin lectura';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('es-AR');
};

const errorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const statusInfo = {
  up: { label: 'Activo', Icon: CircleDot, color: 'text-green-500' },
  no_link: { label: 'Sin enlace', Icon: CircleOff, color: 'text-yellow-500' },
  admin_down: { label: 'Deshabilitado', Icon: CircleMinus, color: 'text-red-500' },
  manual: { label: 'Sin lectura', Icon: Circle, color: 'text-muted-foreground' },
  unknown: { label: 'Desconocido', Icon: Circle, color: 'text-muted-foreground' },
};

const SwitchPortsList = ({ switchId, onStatsChange }) => {
  const { canCreate, canUpdate, canDelete } = usePermissions('assets', 'switches');
  const [loading, setLoading] = useState(true);
  const [ports, setPorts] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, down: 0, admin_down: 0, documented: 0, manual_ports: 0 });
  const [filterOptions, setFilterOptions] = useState({ tipos_dispositivo: [], tipos_conexion: [] });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [onlyDocumented, setOnlyDocumented] = useState(false);
  const [mode, setMode] = useState(null);
  const [selectedPort, setSelectedPort] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const requestSequence = useRef(0);
  const readOnly = mode === 'view';
  const manualName = mode === 'create' || mode === 'copy' || (mode === 'edit' && selectedPort?.is_manual_port);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get(`/assets/switches/${switchId}/ports/stats`);
      setStats(response.data);
      onStatsChange?.(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas de puertos:', error);
    }
  }, [switchId, onStatsChange]);

  const fetchPorts = useCallback(async () => {
    const requestId = ++requestSequence.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '50' });
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (deviceFilter !== 'all') params.append('tipo_dispositivo', deviceFilter);
      if (onlyDocumented) params.append('only_documented', 'true');
      const response = await api.get(`/assets/switches/${switchId}/ports?${params}`);
      if (requestId !== requestSequence.current) return;
      const data = response.data;
      if (page > 1 && page > (data.pages || 1)) {
        setPage(data.pages || 1);
      } else {
        setPorts(data.items);
        setTotal(data.total);
        setTotalPages(data.pages);
      }
    } catch (error) {
      if (requestId === requestSequence.current) toast.error(errorMessage(error, 'No se pudieron cargar los puertos'));
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [switchId, page, search, statusFilter, deviceFilter, onlyDocumented]);

  useEffect(() => {
    fetchStats();
    api.get(`/assets/switches/${switchId}/ports/filter-options`)
      .then(response => setFilterOptions(response.data))
      .catch(() => toast.error('No se pudieron cargar las opciones de puertos'));
  }, [switchId, fetchStats]);

  useEffect(() => { fetchPorts(); return () => { requestSequence.current += 1; }; }, [fetchPorts]);

  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); setSearch(searchInput.trim()); }, 800);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const openForm = (nextMode, port = null) => {
    setSelectedPort(port);
    setForm(port ? { ...formFromPort(port), ...(nextMode === 'copy' ? { port_name: '', port_number: '' } : {}) } : emptyForm());
    setFormError('');
    setMode(nextMode);
  };

  const change = (field, value) => setForm(current => ({ ...current, [field]: value }));

  const refresh = async () => { await Promise.all([fetchStats(), fetchPorts()]); };

  const save = async () => {
    const name = form.port_name.trim();
    if (manualName && !name) { setFormError('Ingresá el nombre del puerto.'); return; }
    if (manualName && form.port_number && (!/^\d+$/.test(form.port_number) || Number(form.port_number) < 0)) {
      setFormError('El número de puerto debe ser un entero igual o mayor que cero.'); return;
    }
    if (form.vlan_id && (!/^\d+$/.test(form.vlan_id) || Number(form.vlan_id) < 1 || Number(form.vlan_id) > 4094)) {
      setFormError('La VLAN debe ser un número entre 1 y 4094.'); return;
    }
    const doc = {
      tipo_dispositivo_id: form.tipo_dispositivo_id ? Number(form.tipo_dispositivo_id) : null,
      tipo_conexion_id: form.tipo_conexion_id ? Number(form.tipo_conexion_id) : null,
      connected_ip: form.connected_ip.trim() || null,
      connected_hostname: form.connected_hostname.trim() || null,
      vlan_id: form.vlan_id ? Number(form.vlan_id) : null,
      user_description: form.user_description.trim() || null,
      user_notes: form.user_notes.trim() || null,
    };
    const manual = { ...doc, port_name: name, port_number: form.port_number ? Number(form.port_number) : null };
    setSaving(true);
    setFormError('');
    try {
      if (mode === 'create' || mode === 'copy') {
        await api.post(`/assets/switches/${switchId}/ports/manual`, manual);
      } else if (selectedPort.is_manual_port) {
        await api.put(`/assets/switches/${switchId}/ports/manual/${selectedPort.doc_id}`, manual);
      } else {
        await api.put(`/assets/switches/${switchId}/ports/${encodeURIComponent(selectedPort.port_name)}/doc`, doc);
      }
      toast.success(mode === 'edit' ? 'Puerto actualizado' : 'Puerto agregado');
      setMode(null);
      await refresh();
    } catch (error) {
      setFormError(errorMessage(error, 'No se pudo guardar el puerto. Revisá los datos e intentá nuevamente.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await api.delete(`/assets/switches/${switchId}/ports/${encodeURIComponent(deleting.port_name)}/doc`);
      toast.success(deleting.is_manual_port ? 'Puerto manual eliminado' : 'Documentación eliminada');
      setDeleting(null);
      await refresh();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo eliminar la documentación'));
    } finally {
      setSaving(false);
    }
  };

  const statItems = [
    ['Total', stats.total, Cable, 'text-primary'],
    ['Activos', stats.active, CircleDot, 'text-green-500'],
    ['Sin enlace', stats.down, CircleOff, 'text-yellow-500'],
    ['Deshabilitados', stats.admin_down || 0, CircleMinus, 'text-red-500'],
    ['Documentados', stats.documented, CheckCircle2, 'text-primary'],
    ['Manuales', stats.manual_ports, Circle, 'text-muted-foreground'],
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Puertos del switch</h2>
          <p className="text-xs text-muted-foreground">Los datos SNMP corresponden a la última lectura; la documentación se carga manualmente.</p>
        </div>
        {canCreate && <Button onClick={() => openForm('create')} data-testid="add-port"><Plus className="h-4 w-4" strokeWidth={1.5} />Agregar puerto</Button>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ports-stats">
        {statItems.map(([label, value, Icon, color]) => (
          <div key={label} className="stats-card flex items-center gap-3">
            <Icon className={`h-6 w-6 shrink-0 ${color}`} strokeWidth={1.5} />
            <div><div className="stats-card-value">{value ?? 0}</div><div className="stats-card-label">{label}</div></div>
          </div>
        ))}
      </div>

      <Card className="card-subtle">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <Label htmlFor="port-search" className="text-xs">Buscar</Label>
            <div className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
              <Input id="port-search" className="pl-9" placeholder="Puerto, IP, hostname o descripción" value={searchInput} onChange={e => setSearchInput(e.target.value)} />
            </div>
          </div>
          <div className="w-[150px]"><Label className="text-xs">Estado</Label>
            <Select value={statusFilter} onValueChange={value => { setPage(1); setStatusFilter(value); }}>
              <SelectTrigger aria-label="Filtrar por estado"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem><SelectItem value="up">Activos</SelectItem>
                <SelectItem value="down">Sin enlace</SelectItem><SelectItem value="admin_down">Deshabilitados</SelectItem>
                <SelectItem value="manual">Manuales</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-[160px]"><Label className="text-xs">Tipo de dispositivo</Label>
            <Select value={deviceFilter} onValueChange={value => { setPage(1); setDeviceFilter(value); }}>
              <SelectTrigger aria-label="Filtrar por tipo de dispositivo"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{filterOptions.tipos_dispositivo.map(item => <SelectItem key={item.id} value={String(item.id)}>{item.nombre}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-2"><Checkbox id="ports-only-doc" checked={onlyDocumented} onCheckedChange={value => { setPage(1); setOnlyDocumented(value === true); }} /><Label htmlFor="ports-only-doc" className="text-xs">Solo documentados</Label></div>
        </CardContent>
      </Card>

      <Card className="card-subtle overflow-hidden"><CardContent className="p-0">
        {loading ? <div className="empty-state"><Loader2 className="h-7 w-7 animate-spin mx-auto text-muted-foreground" /><p className="text-sm text-muted-foreground mt-2">Cargando puertos...</p></div> :
          ports.length === 0 ? <div className="empty-state"><Cable className="h-10 w-10 mx-auto mb-3 text-muted-foreground" strokeWidth={1.5} /><p className="empty-state-text">No se encontraron puertos.</p></div> : <>
            <div className="overflow-x-auto"><table className="table-subtle">
              <thead><tr><th>PUERTO</th><th>ORIGEN</th><th>ESTADO</th><th>VELOCIDAD</th><th>VLAN</th><th>DISPOSITIVO</th><th>HOSTNAME / IP</th><th>DESCRIPCIÓN</th><th>ÚLTIMA LECTURA</th><th className="text-right">ACCIONES</th></tr></thead>
              <tbody>{ports.map(port => {
                const info = statusInfo[port.status_icon] || statusInfo.unknown;
                const Icon = info.Icon;
                return <tr key={`${port.snmp_id ?? 'manual'}-${port.doc_id ?? port.port_name}`}>
                  <td className="font-mono text-xs whitespace-nowrap">{port.port_name}{port.port_number != null ? <span className="text-muted-foreground ml-1">({port.port_number})</span> : null}</td>
                  <td><span className={`badge ${port.is_manual_port ? 'badge-gray' : 'badge-info'}`}>{port.is_manual_port ? 'Manual' : 'SNMP'}</span></td>
                  <td><span className="flex items-center gap-1 whitespace-nowrap"><Icon className={`h-4 w-4 ${info.color}`} strokeWidth={1.5} /><span className="text-xs">{info.label}</span></span></td>
                  <td>{port.speed_display || '—'}</td>
                  <td><span title={port.doc_vlan != null ? 'VLAN documentada' : 'VLAN SNMP'}>{port.doc_vlan ?? port.snmp_vlan ?? '—'}</span>{port.doc_vlan != null && <span className="text-xs text-muted-foreground ml-1">manual</span>}</td>
                  <td>{port.tipo_dispositivo_nombre || '—'}</td>
                  <td className="text-xs"><div>{port.connected_hostname || '—'}</div><div className="font-mono text-muted-foreground">{port.connected_ip || '—'}</div></td>
                  <td className="max-w-[220px] truncate" title={port.user_description || port.snmp_description || ''}>{port.user_description || port.snmp_description || '—'}</td>
                  <td className="text-xs text-muted-foreground whitespace-nowrap">{port.is_manual_port ? '—' : formatDate(port.snmp_last_update)}</td>
                  <td><div className="flex justify-end gap-0.5">
                    <button type="button" className="action-btn" title="Ver" aria-label={`Ver ${port.port_name}`} onClick={() => openForm('view', port)}><Eye className="h-4 w-4" strokeWidth={1.5} /></button>
                    {canUpdate && <button type="button" className="action-btn action-btn-edit" title="Editar" aria-label={`Editar ${port.port_name}`} onClick={() => openForm('edit', port)}><Edit2 className="h-4 w-4" strokeWidth={1.5} /></button>}
                    {canCreate && <button type="button" className="action-btn action-btn-copy" title="Copiar" aria-label={`Copiar ${port.port_name}`} onClick={() => openForm('copy', port)}><Copy className="h-4 w-4" strokeWidth={1.5} /></button>}
                    {canDelete && port.is_documented && <button type="button" className="action-btn action-btn-delete" title={port.is_manual_port ? 'Eliminar puerto manual' : 'Eliminar documentación'} aria-label={`Eliminar ${port.port_name}`} onClick={() => setDeleting(port)}><Trash2 className="h-4 w-4" strokeWidth={1.5} /></button>}
                  </div></td>
                </tr>;
              })}</tbody>
            </table></div>
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t text-sm text-muted-foreground">
              <span>{total} {total === 1 ? 'puerto' : 'puertos'}</span>
              <div className="flex items-center gap-2"><Button variant="outline" size="sm" aria-label="Página anterior" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" strokeWidth={1.5} /></Button><span>{page} / {totalPages || 1}</span><Button variant="outline" size="sm" aria-label="Página siguiente" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" strokeWidth={1.5} /></Button></div>
            </div>
          </>}
      </CardContent></Card>

      <Dialog open={!!mode} onOpenChange={open => { if (!open && !saving) setMode(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="port-dialog" onOpenAutoFocus={event => { event.preventDefault(); document.getElementById('port-dialog-title')?.focus(); }}>
          <DialogHeader>
            <DialogTitle id="port-dialog-title" tabIndex={-1} className="flex items-center gap-2 outline-none"><Cable className="h-5 w-5 text-primary" strokeWidth={1.5} />{mode === 'view' ? 'Ver puerto' : mode === 'edit' ? 'Editar puerto' : mode === 'copy' ? 'Copiar puerto' : 'Agregar puerto'}</DialogTitle>
            <DialogDescription>{mode === 'create' || mode === 'copy' ? 'El puerto se registra en el inventario y no modifica la configuración del switch.' : 'Los datos SNMP son de solo lectura; la información del dispositivo se documenta manualmente.'}</DialogDescription>
          </DialogHeader>
          {selectedPort && !selectedPort.is_manual_port && mode !== 'copy' && <div className="rounded-lg border bg-muted/30 p-3 text-sm space-y-1">
            <p className="font-medium">Última lectura SNMP: {formatDate(selectedPort.snmp_last_update)}</p>
            <p className="text-muted-foreground">Administrativo: {selectedPort.admin_status || '—'} · Operativo: {selectedPort.oper_status || '—'} · Velocidad: {selectedPort.speed_display || '—'}</p>
            <p className="text-muted-foreground">MAC del puerto: {selectedPort.mac_address || '—'} · VLAN SNMP: {selectedPort.snmp_vlan ?? '—'}</p>
          </div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1"><Label htmlFor="port-name">Nombre del puerto {manualName ? '*' : ''}</Label><Input id="port-name" value={form.port_name} onChange={e => change('port_name', e.target.value)} disabled={!manualName || readOnly} placeholder="Ej: GigabitEthernet1/0/1" /></div>
            <div className="space-y-1"><Label htmlFor="port-number">Número</Label><Input id="port-number" type="number" min="0" value={form.port_number} onChange={e => change('port_number', e.target.value)} disabled={!manualName || readOnly} placeholder="Ej: 1" /></div>
            <div className="space-y-1"><Label>Tipo de dispositivo</Label><Select value={form.tipo_dispositivo_id || 'none'} disabled={readOnly} onValueChange={v => change('tipo_dispositivo_id', v === 'none' ? '' : v)}><SelectTrigger aria-label="Tipo de dispositivo"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Sin especificar</SelectItem>{filterOptions.tipos_dispositivo.map(item => <SelectItem key={item.id} value={String(item.id)}>{item.nombre}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1"><Label>Tipo de conexión</Label><Select value={form.tipo_conexion_id || 'none'} disabled={readOnly} onValueChange={v => change('tipo_conexion_id', v === 'none' ? '' : v)}><SelectTrigger aria-label="Tipo de conexión"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Sin especificar</SelectItem>{filterOptions.tipos_conexion.map(item => <SelectItem key={item.id} value={String(item.id)}>{item.nombre}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1"><Label htmlFor="port-ip">IP conectada</Label><Input id="port-ip" value={form.connected_ip} onChange={e => change('connected_ip', e.target.value)} disabled={readOnly} placeholder="Ej: 192.0.2.10" /></div>
            <div className="space-y-1"><Label htmlFor="port-hostname">Hostname</Label><Input id="port-hostname" value={form.connected_hostname} onChange={e => change('connected_hostname', e.target.value)} disabled={readOnly} placeholder="Ej: AP-recepción" /></div>
            <div className="space-y-1"><Label htmlFor="port-vlan">VLAN documentada</Label><Input id="port-vlan" type="number" min="1" max="4094" value={form.vlan_id} onChange={e => change('vlan_id', e.target.value)} disabled={readOnly} placeholder="1 a 4094" /></div>
            <div className="space-y-1 sm:col-span-2"><Label htmlFor="port-description">Descripción</Label><Input id="port-description" value={form.user_description} onChange={e => change('user_description', e.target.value)} disabled={readOnly} /></div>
            <div className="space-y-1 sm:col-span-2"><Label htmlFor="port-notes">Notas</Label><Textarea id="port-notes" rows={2} value={form.user_notes} onChange={e => change('user_notes', e.target.value)} disabled={readOnly} /></div>
          </div>
          {formError && <p role="alert" className="text-sm text-destructive">{formError}</p>}
          <DialogFooter><Button variant="outline" onClick={() => setMode(null)} disabled={saving}>{readOnly ? 'Cerrar' : 'Cancelar'}</Button>{!readOnly && <Button onClick={save} disabled={saving}>{saving ? 'Guardando...' : mode === 'edit' ? 'Guardar cambios' : 'Agregar puerto'}</Button>}</DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={open => { if (!open && !saving) setDeleting(null); }}>
        <DialogContent><DialogHeader><DialogTitle>Confirmar eliminación</DialogTitle><DialogDescription>{deleting?.is_manual_port ? `Se eliminará el puerto manual ${deleting.port_name} del inventario.` : `Se eliminará la documentación de ${deleting?.port_name}. El puerto SNMP seguirá visible.`}</DialogDescription></DialogHeader><DialogFooter><Button variant="outline" onClick={() => setDeleting(null)} disabled={saving}>Cancelar</Button><Button variant="destructive" onClick={remove} disabled={saving}>{saving ? 'Eliminando...' : 'Eliminar'}</Button></DialogFooter></DialogContent>
      </Dialog>
    </div>
  );
};

export default SwitchPortsList;
